"use client";

import clsx from "clsx";
import {
  Autocomplete,
  Button,
  Input,
  Popover,
  ListBox,
  ListBoxItem,
  Label,
  SearchField,
  Select as AriaSelect,
  SelectValue,
} from "react-aria-components";
import { useFilter } from "react-aria";
import ChevronDown16 from "@ui/icons/16x16/chevron-down-16.svg";
import { X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { FieldContainer } from "./FieldContainer";
import { ScrollFadeOverlay } from "./ScrollFadeOverlay";
import {
  fieldErrorClassname,
  fieldInputVariants,
  fieldLabelClassname,
  selectContentClassname,
  selectItemClassname,
  selectTriggerClassname,
} from "./field-variants";
import type { BaseFieldProps } from "./Input";
import { useFieldContext } from "../hooks/form-context";
import { useScrollFade } from "../hooks/useScrollFade";
import { useTranslations } from "next-intl";
import type { FormFieldOption } from "../shared/types";

const SEARCH_THRESHOLD = 6;

export interface SelectProps extends BaseFieldProps {
  options?: readonly FormFieldOption[];
  unselectedLabel?: string;
  portalContainer?: React.RefObject<Element | null>;
}

export const Select = ({ label, options = [], unselectedLabel, required, name, portalContainer }: SelectProps) => {
  const field = useFieldContext();
  const t = useTranslations("ClientKeys.contact_sales_page.form");
  const value = (field.state.value as string) || "";
  const [isOpen, setIsOpen] = useState(false);
  const listBoxRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown, updateScrollState, resetScrollState } = useScrollFade(listBoxRef);

  const { contains } = useFilter({ sensitivity: "base" });

  const showSearch = options.length >= SEARCH_THRESHOLD;

  // Find the selected option from the full list (not filtered) to preserve display while searching
  const selectedOption = useMemo(() => options.find(opt => opt.value === value), [options, value]);

  // Separate "Other" option to show as fallback in empty state
  const otherOption = useMemo(() => options.find(opt => opt.value.toLowerCase() === "other"), [options]);

  const listBoxItems = useMemo(() => options.map(opt => ({ id: opt.value, ...opt })), [options]);

  const handleSelectOther = () => {
    if (otherOption) {
      field.handleChange(otherOption.value);
      setIsOpen(false);
      resetScrollState();
      field.handleBlur();
    }
  };

  const emptyStateElement = otherOption ? (
    <button
      type='button'
      onClick={handleSelectOther}
      className={clsx(selectItemClassname, "tw-rounded-lg tw-w-full tw-text-left hover:bg-dark")}
    >
      <span className='tw-block tw-truncate'>{otherOption.label}</span>
    </button>
  ) : (
    <div className='tw-px-3 tw-py-2 tw-text-sm text-light'>{t("empty_states.no_results_found")}</div>
  );

  const labelElement = (
    <div className='tw-flex tw-justify-between'>
      <Label className={fieldLabelClassname} htmlFor={name}>
        {label}
      </Label>
      {!required && <span className='tw-text-[0.75rem] tw-text-black tw-opacity-40'>{t("labels.optional")}</span>}
    </div>
  );

  const errorElement = !field.state.meta.isValid &&
    field.state.meta.isBlurred &&
    field.state.meta.errors.length > 0 && (
      <div className={fieldErrorClassname} role='alert'>
        {field.state.meta.errors[0]}
      </div>
    );

  const listBoxContent = (
    <ListBox
      ref={listBoxRef}
      className={clsx("tw-overflow-auto tw-outline-none", showSearch && "tw-max-h-64 tw-pt-1")}
      items={listBoxItems}
      onScroll={updateScrollState}
      renderEmptyState={() => emptyStateElement}
    >
      {item => (
        <ListBoxItem
          id={item.value}
          textValue={item.label}
          className={({ isSelected, isHovered }) =>
            clsx(selectItemClassname, "tw-rounded-lg", isSelected && "tw-font-semibold", isHovered && "bg-dark")
          }
        >
          <span className='tw-block tw-truncate'>{item.label}</span>
        </ListBoxItem>
      )}
    </ListBox>
  );

  return (
    <FieldContainer>
      {labelElement}
      <AriaSelect
        name={name}
        value={value || null}
        isOpen={isOpen}
        onChange={key => {
          if (key !== null) {
            field.handleChange(key as string);
          }
        }}
        onOpenChange={open => {
          setIsOpen(open);
          if (open) {
            // Update scroll state after DOM renders
            setTimeout(updateScrollState, 0);
          } else {
            resetScrollState();
            field.handleBlur();
          }
        }}
        aria-label={label}
      >
        <Button id={name} className={clsx(selectTriggerClassname, "tw-text-left")}>
          <SelectValue className='tw-flex-1 tw-truncate'>
            {selectedOption ? (
              <span className='tw-truncate'>{selectedOption.label}</span>
            ) : (
              <span className='tw-text-gray-400'>{unselectedLabel}</span>
            )}
          </SelectValue>
          <ChevronDown16
            className={clsx("tw-ml-2 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 text-lighter", {
              "tw-rotate-180": isOpen,
            })}
          />
        </Button>
        <Popover
          className={clsx(selectContentClassname, "tw-flex tw-flex-col tw-w-[var(--trigger-width)]")}
          offset={4}
          placement='bottom'
          containerPadding={0}
          UNSTABLE_portalContainer={portalContainer?.current ?? undefined}
        >
          {showSearch ? (
            <Autocomplete filter={contains}>
              <SearchField
                aria-label={t("placeholders.search")}
                className='tw-px-3 tw-pt-3 tw-pb-3'
                onChange={() => setTimeout(updateScrollState, 0)}
              >
                <div
                  className={clsx(
                    fieldInputVariants(),
                    "tw-flex tw-items-center tw-gap-2 tw-h-9 tw-py-2 tw-px-3 tw-text-sm",
                  )}
                >
                  <Input
                    placeholder={t("placeholders.search")}
                    className='tw-flex-1 tw-min-w-0 tw-border-none tw-outline-none tw-bg-transparent tw-text-sm placeholder:tw-text-gray-400 [&::-webkit-search-cancel-button]:tw-hidden'
                  />
                  <Button className='tw-p-0.5 tw-rounded tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-black/5 tw-transition-colors group-empty:tw-invisible'>
                    <X className='tw-size-3' aria-hidden />
                  </Button>
                </div>
              </SearchField>
              <div className='tw-border-t border-default' />
              <ScrollFadeOverlay canScrollUp={canScrollUp} canScrollDown={canScrollDown}>
                {listBoxContent}
              </ScrollFadeOverlay>
            </Autocomplete>
          ) : (
            <ScrollFadeOverlay canScrollUp={canScrollUp} canScrollDown={canScrollDown}>
              {listBoxContent}
            </ScrollFadeOverlay>
          )}
        </Popover>
      </AriaSelect>
      {errorElement}
    </FieldContainer>
  );
};

export const FieldSelect = Select;
